import dayjs from "dayjs";

const AMOUNT_LOCALE = "es-PY";

export const currencyFractionDigits = (currency) => {
  if (!currency) return 2;
  try {
    return (
      new Intl.NumberFormat(AMOUNT_LOCALE, {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
};

export const formatFinancialNumber = (value, currency) => {
  const number = Number(value) || 0;
  const digits = currencyFractionDigits(currency);

  return number.toLocaleString(AMOUNT_LOCALE, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const formatFinancialAmount = (value, currency) => {
  const formatted = formatFinancialNumber(value, currency);

  return currency ? `${formatted} ${currency}` : formatted;
};

export const parseFinancialAmount = (text) => {
  if (text == null) return Number.NaN;
  const normalized = String(text).trim().replace(/\s/g, "");
  if (normalized === "") return Number.NaN;

  if (normalized.includes(",") && normalized.includes(".")) {
    return Number(normalized.replace(/\./g, "").replace(",", "."));
  }
  if (normalized.includes(",")) {
    return Number(normalized.replace(/\./g, "").replace(",", "."));
  }
  if (/^\d{1,3}(\.\d{3})+$/.test(normalized)) {
    return Number(normalized.replace(/\./g, ""));
  }

  return Number(normalized);
};

export const accountNetBalance = (account) => Number(account?.balance?.available ?? 0);

const accountSaldoGroup = (saldo) => {
  if (saldo < 0) return 0;
  if (saldo > 0) return 1;
  return 2;
};

export const compareAccountsBySaldo = (a, b) => {
  const aSaldo = accountNetBalance(a);
  const bSaldo = accountNetBalance(b);
  const groupDiff = accountSaldoGroup(aSaldo) - accountSaldoGroup(bSaldo);
  if (groupDiff !== 0) return groupDiff;

  const absDiff = Math.abs(bSaldo) - Math.abs(aSaldo);
  if (absDiff !== 0) return absDiff;

  return (a.target_name || "").localeCompare(b.target_name || "");
};

const isoDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const invoiceLedgerSortValue = (invoice) => {
  const value = invoice?.membership_due_on || invoice?.due_date || invoice?.created_at;
  if (!value) return Number.POSITIVE_INFINITY;
  const time = Date.parse(value);
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

export const compareInvoicesByDateAsc = (a, b) => invoiceLedgerSortValue(a) - invoiceLedgerSortValue(b);

export const formatMembershipMonth = (value, intl) => {
  if (!value || !intl) return "—";
  const month = intl.formatDate(value, { month: "long", timeZone: "UTC" });
  const year = intl.formatDate(value, { year: "numeric", timeZone: "UTC" });
  if (!month || !year) return "—";
  return `${month.charAt(0).toUpperCase()}${month.slice(1)}-${year}`;
};

export const invoiceLedgerDate = (invoice, intl, membershipTab = false) => {
  if (membershipTab && invoice?.membership_due_on) {
    return formatMembershipMonth(invoice.membership_due_on, intl);
  }
  const value = invoice?.membership_due_on || invoice?.due_date || invoice?.created_at;
  return value ? dayjs(value).format("DD/MM/YYYY") : "—";
};

export const isFutureMembershipCharge = (invoice, now = new Date()) => {
  if (invoice?.balance_available === false) return true;
  if (invoice?.balance_available === true) return false;
  const due = invoice?.membership_due_on;
  if (!due) return false;
  return isoDate(due) > isoDate(now);
};
